import { type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { Card } from '../ui/Card';
import { TextArea } from '../ui/TextArea';
import { Button } from '../ui/Button';
import { Banner } from '../ui/Banner';
import { putSecret } from '../../lib/api';

export default function Home() {
  const navigate = useNavigate();
  const [secret, setSecret] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = secret.trim();
    if (!trimmed) return;
    setError(null);
    setIsSubmitting(true);
    try {
      const { id } = await putSecret(trimmed);
      navigate(`/show/${id}`);
    } catch (err) {
      setIsSubmitting(false);
      setError(
        err instanceof Error
          ? `Couldn't seal your secret: ${err.message}`
          : "Couldn't seal your secret. Please try again.",
      );
    }
  };

  return (
    <Card>
      <h1 className="font-display text-3xl font-semibold tracking-tight text-text sm:text-[2rem]">
        Send a one-time secret
      </h1>
      <p className="mt-3 text-sm text-text-muted sm:text-base">
        Paste your message here. We&rsquo;ll give you a shareable link that self-destructs after the first
        read &mdash; or in 10 minutes, whichever comes first.
      </p>
      <form className="mt-6 flex flex-col gap-4" onSubmit={onSubmit}>
        <TextArea
          id="secret"
          name="secret"
          placeholder="Type or paste your secret…"
          rows={5}
          autoFocus
          showCount
          value={secret}
          onChange={(e) => setSecret(e.target.value)}
        />
        {error && (
          <Banner tone="danger" icon={<AlertCircle size={16} />}>
            {error}
          </Banner>
        )}
        <Button type="submit" fullWidth loading={isSubmitting} disabled={!secret.trim()}>
          {isSubmitting ? 'Sealing your secret…' : 'Create secret link'}
        </Button>
      </form>
    </Card>
  );
}
